import { EuiBadge } from '@elastic/eui';
import './styles.scss';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';

import { ClearButton } from '../../components/ClearButton';
import { LocalCache } from 'apollo-client/queries/LocalCache';
import { MouseEventHandler } from 'react';
import { THEME } from 'theme';

export interface FilterListProps {
  filters?: LocalCache;
  onRemoveChips: <A extends keyof LocalCache>(filterName: A, value: string) => void;
  onResetFilters: () => void;
}

export const FilterChip: (p: {
  children: React.ReactNode;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) => JSX.Element = ({ children, onClick }) => (
  <EuiBadge
    iconType={typeof onClick !== 'undefined' ? 'cross' : ''}
    iconSide="right"
    color={THEME.GREY_1}
    iconOnClick={onClick}
    iconOnClickAriaLabel="Click this icon to..."
  >
    {children}
  </EuiBadge>
);

export const FilterChips: (x: FilterListProps) => null | JSX.Element = ({ filters, onRemoveChips, onResetFilters }) => {
  // TODO ADD COUNTRY_FILTER
  const { formatMessage, formatNumber } = useIntl();
  const publishFromFmt = filters?.publishDateFromISO8601
    ? format(new Date(`${filters.publishDateFromISO8601}T12:00:00`), 'dd/MM/yyyy')
    : null;
  const publishToFmt = filters?.publishDateToISO8601
    ? format(new Date(`${filters.publishDateToISO8601}T12:00:00`), 'dd/MM/yyyy')
    : null;

  const filtersArePresent =
    filters?.brandNames?.length ||
    filters?.modelNames?.length ||
    filters?.powerSupplies?.length ||
    filters?.tractionTypes?.length ||
    filters?.gearTypes?.length ||
    filters?.vendorType ||
    filters?.regions?.length ||
    filters?.isListingActive ||
    filters?.provinces?.length ||
    filters?.isVehicleCoveredByWarranty ||
    filters?.interiorColors?.length ||
    filters?.coatingColors?.length ||
    typeof filters?.isPriceTiedToLoan !== 'undefined' ||
    typeof filters?.isVATDeductible !== 'undefined' ||
    typeof filters?.isPictureAvailable !== 'undefined' ||
    filters?.vehicleRegistrationDateISO8601 ||
    filters?.engineDisplacementInCM3 ||
    filters?.powerInHP ||
    filters?.priceInEUR ||
    filters?.modelVersions ||
    filters?.websites ||
    filters?.optionals ||
    filters?.vehicleTypes ||
    filters?.country ||
    filters?.vehicleKM ||
    filters?.publishDateFromISO8601 ||
    filters?.publishDateToISO8601;

  return !filtersArePresent ? null : (
    <div className="filterList__secondaryFilters">
      {filters?.publishDateFromISO8601 || filters?.publishDateToISO8601 ? (
        <FilterChip
          onClick={(): void => {
            onRemoveChips('publishDateFromISO8601', '');
            onRemoveChips('publishDateToISO8601', '');
          }}
        >
          {formatMessage({ id: 'filters.publishDate.chips', defaultMessage: 'Publish date' })}:{' '}
          {publishFromFmt && publishToFmt
            ? `${publishFromFmt} - ${publishToFmt}`
            : publishFromFmt
            ? `${formatMessage({ id: 'filters.publishDate.from' })} ${publishFromFmt}`
            : `${formatMessage({ id: 'filters.publishDate.to' })} ${publishToFmt}`}
        </FilterChip>
      ) : null}
      {filters?.vehicleRegistrationDateISO8601?.min || filters?.vehicleRegistrationDateISO8601?.max ? (
        <FilterChip
          onClick={(): void => {
            onRemoveChips('vehicleRegistrationDateISO8601', '');
          }}
        >
          {formatMessage({ id: 'global.from' })}:{' '}
          {new Date(filters?.vehicleRegistrationDateISO8601?.min as string).getFullYear()}{' '}
          {formatMessage({ id: 'global.to' })}{' '}
          {new Date(filters?.vehicleRegistrationDateISO8601?.max as string).getFullYear()}
        </FilterChip>
      ) : null}
      {filters?.engineDisplacementInCM3?.min || filters?.engineDisplacementInCM3?.max ? (
        <FilterChip
          onClick={(): void => {
            onRemoveChips('engineDisplacementInCM3', '');
          }}
        >
          {formatMessage({ id: 'global.from' })}: {filters.engineDisplacementInCM3.min?.toString()}{' '}
          {formatMessage({ id: 'global.to' })} {filters.engineDisplacementInCM3.max?.toString() + ' CM3'}
        </FilterChip>
      ) : null}
      {filters?.powerInHP?.min || filters?.powerInHP?.max ? (
        <FilterChip
          onClick={(): void => {
            onRemoveChips('powerInHP', '');
          }}
        >
          {formatMessage({ id: 'global.from' })}: {filters.powerInHP.min?.toString()}{' '}
          {formatMessage({ id: 'global.to' })} {filters.powerInHP.max?.toString() + ' CV'}
        </FilterChip>
      ) : null}
      {filters?.vehicleKM?.min || filters?.vehicleKM?.max ? (
        <FilterChip
          onClick={(): void => {
            onRemoveChips('vehicleKM', '');
          }}
        >
          {formatMessage({ id: 'global.from' })}: {filters.vehicleKM.min?.toString()}{' '}
          {formatMessage({ id: 'global.to' })} {filters.vehicleKM.max?.toString() + ' KM'}
        </FilterChip>
      ) : null}
      {filters?.priceInEUR?.min || filters?.priceInEUR?.max ? (
        <FilterChip
          onClick={(): void => {
            onRemoveChips('priceInEUR', '');
          }}
        >
          {formatMessage({ id: 'global.from' })}: {filters.priceInEUR.min} {formatMessage({ id: 'global.to' })}{' '}
          {formatNumber(filters.priceInEUR.max as number, {
            currency: 'EUR',
            style: 'currency',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })}{' '}
        </FilterChip>
      ) : null}
      {filters?.brandNames?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('brandNames', x);
          }}
        >
          {x}
        </FilterChip>
      ))}
      {filters?.optionals?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('optionals', x);
          }}
        >
          {x}
        </FilterChip>
      ))}
      {filters?.vehicleTypes?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('vehicleTypes', x);
          }}
        >
          {formatMessage({ id: `filters.vehicleType.${x}` })}
        </FilterChip>
      ))}
      {filters?.gearTypes?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('gearTypes', x);
          }}
        >
          {x}
        </FilterChip>
      ))}
      {filters?.powerSupplies?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('powerSupplies', x);
          }}
        >
          {x}
        </FilterChip>
      ))}
      {filters?.modelNames?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('modelNames', x);
          }}
        >
          {x}
        </FilterChip>
      ))}
      {filters?.modelVersions?.map(({ value, label }) => (
        <FilterChip
          key={value}
          onClick={(): void => {
            onRemoveChips('modelVersions', value);
          }}
        >
          {label}
        </FilterChip>
      ))}
      {filters?.tractionTypes?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('tractionTypes', x);
          }}
        >
          {x}
        </FilterChip>
      ))}
      {filters?.vendorType ? (
        <FilterChip onClick={(): void => {}}>{formatMessage({ id: `vendorType.${filters?.vendorType}` })}</FilterChip>
      ) : null}
      {filters?.provinces?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('provinces', x);
          }}
        >
          {x}
        </FilterChip>
      ))}
      {filters?.regions?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('regions', x);
          }}
        >
          {x}
        </FilterChip>
      ))}
      {filters?.priceLabels?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('priceLabels', x);
          }}
        >
          {formatMessage({ id: `priceIndicators.${x}` })}
        </FilterChip>
      ))}
      {filters?.interiorColors?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('interiorColors', x);
          }}
        >
          {formatMessage({ id: 'filters.interiorColors.chips' })}: {x}
        </FilterChip>
      ))}
      {filters?.coatingColors?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('coatingColors', x);
          }}
        >
          {formatMessage({ id: 'filters.coatingColors.chips' })}: {x}
        </FilterChip>
      ))}
      {filters?.websites?.map((x: string) => (
        <FilterChip
          key={x}
          onClick={(): void => {
            onRemoveChips('websites', x);
          }}
        >
          {x}
        </FilterChip>
      ))}
      <FilterChip onClick={(): void => {}}>
        {formatMessage({ id: `filters.isListingActive.chips.${filters?.isListingActive}` })}
      </FilterChip>
      {typeof filters?.isPriceTiedToLoan !== 'undefined' ? (
        <FilterChip
          onClick={(): void => {
            onRemoveChips('isPriceTiedToLoan', '');
          }}
        >
          {filters.isPriceTiedToLoan
            ? formatMessage({ id: 'filters.isPriceTiedToLoan.true.chip' })
            : formatMessage({ id: 'filters.isPriceTiedToLoan.false.chip' })}
        </FilterChip>
      ) : null}
      {typeof filters?.isVehicleCoveredByWarranty !== 'undefined' ? (
        <FilterChip
          onClick={(): void => {
            onRemoveChips('isVehicleCoveredByWarranty', '');
          }}
        >
          {filters.isVehicleCoveredByWarranty
            ? formatMessage({ id: 'filters.isVehicleCoveredByWarranty.true.chip' })
            : formatMessage({ id: 'filters.isVehicleCoveredByWarranty.false.chip' })}
        </FilterChip>
      ) : null}
      {typeof filters?.isPictureAvailable !== 'undefined' ? (
        <FilterChip
          onClick={(): void => {
            onRemoveChips('isPictureAvailable', '');
          }}
        >
          {filters?.isPictureAvailable
            ? formatMessage({ id: 'filters.isPictureAvailable.true.chip' })
            : formatMessage({ id: 'filters.isPictureAvailable.false.chip' })}
        </FilterChip>
      ) : null}
      {typeof filters?.isVATDeductible !== 'undefined' ? (
        <FilterChip
          onClick={(): void => {
            onRemoveChips('isVATDeductible', '');
          }}
        >
          {filters?.isVATDeductible
            ? formatMessage({ id: 'filters.isVATDeductible.true.chip' })
            : formatMessage({ id: 'filters.isVATDeductible.false.chip' })}
        </FilterChip>
      ) : null}
      {typeof filters?.country !== 'undefined' ? (
        <FilterChip
          onClick={(): void => {
            onRemoveChips('country', '');
          }}
        >
          {filters?.country}
        </FilterChip>
      ) : null}
      <ClearButton onClick={onResetFilters} />
    </div>
  );
};
